import React, { useState, useEffect, useCallback } from 'react';

import { PlusOutlined } from '@ant-design/icons';
import { Card, Table, Tag, Button, Space, Popconfirm, Modal, Form, DatePicker, InputNumber, Input, Descriptions, Spin, message } from 'antd';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import ListPageHeader from '@shared/ListPageHeader';

import { billingService } from '@services/operator/billingService';

const STATUS_COLORS = {
  open: 'blue',
  closed: 'orange',
  invoiced: 'green',
};

export default function BillingsShow() {
  const { t } = useTranslation();
  const { id } = useParams();
  const [loading, setLoading] = useState(true);
  const [billing, setBilling] = useState(null);
  const [billingItems, setBillingItems] = useState([]);
  const [addItemModal, setAddItemModal] = useState(false);
  const [addItemForm] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);

  const fetchBilling = useCallback(async () => {
    try {
      setLoading(true);
      const res = await billingService.get(id);
      if (res.success) {
        setBilling(res.data);
        setBillingItems(res.data.billing_items || []);
      }
    } catch (error) {
      console.error('Failed to fetch billing:', error);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchBilling();
  }, [fetchBilling]);

  const handleStatusChange = async (newStatus) => {
    try {
      const res = await billingService.updateStatus(id, newStatus);
      if (res.success) {
        message.success(t('operator.billings.messages.status_updated'));
        fetchBilling();
      } else {
        message.error(t(`shared.errors.${res.message}`, { defaultValue: res.message }));
      }
    } catch (error) {
      message.error(t('operator.billings.messages.status_update_failed'));
    }
  };

  const handleAddItemSubmit = async () => {
    try {
      const values = await addItemForm.validateFields();
      setSubmitting(true);

      const data = {
        billing_date: values.billing_date.format('YYYY-MM-DD'),
        description: values.description,
        billable_units: values.billable_units,
        unit_price: values.unit_price,
      };

      const res = await billingService.addItem(id, data);
      if (res.success) {
        message.success(t('operator.billings.messages.item_added'));
        setAddItemModal(false);
        fetchBilling();
      } else {
        message.error(t(`shared.errors.${res.message}`, { defaultValue: res.message }));
      }
    } catch (error) {
      if (error.errorFields) return;
      message.error(t('operator.billings.messages.item_add_failed'));
    } finally {
      setSubmitting(false);
    }
  };

  const itemColumns = [
    {
      title: t('operator.billings.fields.billing_date'),
      dataIndex: 'billing_date',
      key: 'billing_date',
    },
    {
      title: t('operator.billings.fields.billing_type'),
      dataIndex: 'billing_type',
      key: 'billing_type',
      render: (type) => (
        <Tag color={type === 'storage' ? 'blue' : 'purple'}>
          {t(`operator.billings.types.${type}`)}
        </Tag>
      ),
    },
    {
      title: t('operator.billings.fields.storage_mode'),
      dataIndex: 'storage_mode',
      key: 'storage_mode',
      render: (mode) => mode ? t(`enums.merchant.storage_billing_mode.${mode}`, { defaultValue: mode }) : '-',
    },
    {
      title: t('operator.billings.fields.description'),
      key: 'description',
      render: (_, record) => record.breakdown?.description || '-',
    },
    {
      title: t('operator.billings.fields.billable_units'),
      dataIndex: 'billable_units',
      key: 'billable_units',
      align: 'right',
      render: (val) => Number(val).toLocaleString(),
    },
    {
      title: t('operator.billings.fields.unit_price'),
      dataIndex: 'unit_price',
      key: 'unit_price',
      align: 'right',
      render: (val) => Number(val).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 4 }),
    },
    {
      title: t('operator.billings.fields.amount'),
      dataIndex: 'amount',
      key: 'amount',
      align: 'right',
      render: (val) => Number(val).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
    },
  ];

  if (loading || !billing) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
        <Spin size="large" />
      </div>
    );
  }

  const statusActions = [];

  if (billing.status === 'open') {
    statusActions.push(
      <Popconfirm
        key="close"
        title={t('operator.billings.confirm.close')}
        onConfirm={() => handleStatusChange('closed')}
      >
        <Button type="primary">{t('operator.billings.actions.close')}</Button>
      </Popconfirm>
    );
  }

  if (billing.status === 'closed') {
    statusActions.push(
      <Popconfirm
        key="invoice"
        title={t('operator.billings.confirm.invoice')}
        onConfirm={() => handleStatusChange('invoiced')}
      >
        <Button type="primary">{t('operator.billings.actions.invoice')}</Button>
      </Popconfirm>
    );
    statusActions.push(
      <Popconfirm
        key="reopen"
        title={t('operator.billings.confirm.reopen')}
        onConfirm={() => handleStatusChange('open')}
      >
        <Button>{t('operator.billings.actions.reopen')}</Button>
      </Popconfirm>
    );
  }

  return (
    <div style={{ padding: '24px' }}>
      <ListPageHeader items={['merchant_settings', 'merchant_settings.billings', { label: billing.name }]} />

      <Card
        style={{ marginBottom: 16 }}
        extra={<Space>{statusActions}</Space>}
      >
        <Descriptions column={2} size="small" bordered>
          <Descriptions.Item label={t('operator.billings.fields.name')}>{billing.name}</Descriptions.Item>
          <Descriptions.Item label={t('operator.billings.show.merchant_name')}>{billing.merchant_name}</Descriptions.Item>
          <Descriptions.Item label={t('operator.billings.fields.period')}>{billing.period_start} ~ {billing.period_end}</Descriptions.Item>
          <Descriptions.Item label={t('operator.billings.fields.status')}>
            <Tag color={STATUS_COLORS[billing.status]}>
              {t(`operator.billings.statuses.${billing.status}`)}
            </Tag>
          </Descriptions.Item>
          <Descriptions.Item label={t('operator.billings.fields.total_amount')}>
            {Number(billing.total_amount).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </Descriptions.Item>
          <Descriptions.Item label={t('operator.billings.fields.items_count')}>{billing.items_count}</Descriptions.Item>
        </Descriptions>
      </Card>

      <Card
        title={t('operator.billings.show.items_title')}
        extra={
          billing.status === 'open' && (
            <Button
              icon={<PlusOutlined />}
              onClick={() => { setAddItemModal(true); addItemForm.resetFields(); }}
            >
              {t('operator.billings.add_item.title')}
            </Button>
          )
        }
      >
        <Table
          columns={itemColumns}
          dataSource={billingItems}
          rowKey="id"
          pagination={false}
          size="middle"
        />
      </Card>

      <Modal
        title={t('operator.billings.add_item.title')}
        open={addItemModal}
        onOk={handleAddItemSubmit}
        onCancel={() => setAddItemModal(false)}
        confirmLoading={submitting}
        destroyOnClose
      >
        <Form form={addItemForm} layout="vertical">
          <Form.Item
            name="billing_date"
            label={t('operator.billings.fields.billing_date')}
            rules={[{ required: true, message: t('common.validation.required') }]}
          >
            <DatePicker
              style={{ width: '100%' }}
              disabledDate={(current) => {
                if (!billing) return false;
                return current < dayjs(billing.period_start) ||
                       current > dayjs(billing.period_end);
              }}
            />
          </Form.Item>

          <Form.Item
            name="description"
            label={t('operator.billings.add_item.description')}
            rules={[{ required: true, message: t('common.validation.required') }]}
          >
            <Input.TextArea rows={2} />
          </Form.Item>

          <Form.Item
            name="billable_units"
            label={t('operator.billings.fields.billable_units')}
            rules={[{ required: true, message: t('common.validation.required') }]}
          >
            <InputNumber style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item
            name="unit_price"
            label={t('operator.billings.fields.unit_price')}
            rules={[{ required: true, message: t('common.validation.required') }]}
          >
            <InputNumber style={{ width: '100%' }} step={0.01} />
          </Form.Item>

          <Form.Item label={t('operator.billings.fields.amount')}>
            <Form.Item noStyle shouldUpdate={(prev, cur) =>
              prev.billable_units !== cur.billable_units || prev.unit_price !== cur.unit_price
            }>
              {({ getFieldValue }) => {
                const units = getFieldValue('billable_units') || 0;
                const price = getFieldValue('unit_price') || 0;
                const amount = (units * price).toFixed(2);
                return <InputNumber style={{ width: '100%' }} value={amount} disabled />;
              }}
            </Form.Item>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
